/**
 * The Flow canvas, as a read-only example you can pan, inspect and simulate —
 * the concept's FlowEditor (`flow/Node.jsx`, `Edges.jsx`, `blueprint-flow.css`)
 * in its "run" posture, ported to Tailwind for the public page.
 *
 * ## READ-ONLY ON PURPOSE
 *
 * The product's editor adds steps from a "+" on every free path, suggests the
 * next step, runs preflight checks and launches. None of that belongs on a
 * marketing page: a builder that cannot save is a toy, and a toy that looks
 * like the product invites "why can't I keep this?". So the canvas here shows
 * one finished flow and lets the reader do the three things that explain it —
 * move around, press a step to read its settings, and press Simulate to watch
 * an example person walk it. The words say the product is where you build one.
 *
 * ## WHAT THE SIMULATION IS
 *
 * `lib/flow-demo.ts` walks the example person through the graph with the
 * engine's own rules — a wait until the next 10:00, quiet hours, the
 * frequency cap counting campaign messages too, the holdout getting nothing,
 * the goal ending the journey the moment an order lands. Nothing is sent and
 * nothing is requested: it is arithmetic in the browser, and the panel's first
 * line says "made-up people" and "Simulate sends nothing". The product's Test button is the same idea, run against a
 * real person in the reader's own workspace — and it sends nothing either.
 *
 * ## KEYBOARD AND SMALL SCREENS
 *
 * Every step on the canvas is a real button (`aria-pressed` for the one being
 * read). The canvas itself takes focus: arrow keys pan, + and − zoom, 0 fits —
 * `canvasKeyAction` is the pure mapping, tested by the site suite. Dragging
 * pans with a mouse, pen or finger (pointer capture; no scroll is hijacked,
 * and the wheel is left alone so the page still scrolls over the canvas).
 * Below 640px the canvas is replaced by the same steps as a list — the
 * product's own fallback below that width — and Simulate highlights the list.
 *
 * Under `prefers-reduced-motion` the simulation reveals every step at once and
 * nothing pulses.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';

import { cn } from '@wend/ui';

import {
  CANVAS,
  FLOW_EDGES,
  FLOW_NAME,
  FLOW_NODES,
  NODE_TYPE_LABEL,
  ROUND_W,
  SAMPLE_PEOPLE,
  canvasKeyAction,
  clampScale,
  curve,
  inPoint,
  isRound,
  nodeById,
  portPoint,
  walk,
  type DemoFlowNode,
  type FlowNodeType,
} from '../lib/flow-demo';

const focus = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b';
const toolButton = cn(
  'inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-line-strong bg-white/[0.04] px-3 text-[0.8rem] text-text transition-colors hover:bg-white/[0.09]',
  focus,
);

function Icon({ type, channel }: { type: FlowNodeType; channel?: 'whatsapp' | 'email' }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  let path: ReactNode;
  if (type === 'message' && channel === 'email') path = <><rect x="3" y="5" width="18" height="14" rx="2" {...common} /><path d="M3 7l9 6 9-6" {...common} /></>;
  else if (type === 'message') path = <path d="M4 18l1.5-4A7.5 7.5 0 1 1 9 18.5z" {...common} />;
  else if (type === 'trigger') path = <path d="M7 5l11 7-11 7z" {...common} />;
  else if (type === 'split') path = <path d="M5 12h5l4-6h5M10 12l4 6h5" {...common} />;
  else if (type === 'wait') path = <><circle cx="12" cy="12" r="8" {...common} /><path d="M12 8v4l3 2" {...common} /></>;
  else if (type === 'condition') path = <path d="M12 4l8 8-8 8-8-8z" {...common} />;
  else if (type === 'update') path = <><circle cx="12" cy="8" r="3.5" {...common} /><path d="M5 20a7 7 0 0 1 14 0" {...common} /></>;
  else if (type === 'goal') path = <path d="M6 21V4h11l-2 4 2 4H6" {...common} />;
  else path = <path d="M14 5h5v14h-5M10 8l-4 4 4 4M6 12h9" {...common} />;
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="flex-none">
      {path}
    </svg>
  );
}

const HEAD_TONE: Readonly<Record<FlowNodeType, string>> = {
  trigger: 'text-info',
  message: 'text-good',
  wait: 'text-white',
  condition: 'text-white',
  split: 'text-white',
  update: 'text-brand-ink',
  goal: 'text-good',
  exit: 'text-bad',
};

/** `{{name}}` drawn as a variable chip, the rest as text. */
function Body({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\{\{\w+\}\})/).map((part, index) =>
        /^\{\{\w+\}\}$/.test(part) ? (
          <span key={index} className="whitespace-nowrap rounded-[5px] bg-brand-b/28 px-1.5 font-mono text-[0.72rem] text-brand-ink-strong">
            {part.slice(2, -2)}
          </span>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  );
}

interface NodeLook {
  readonly selected: boolean;
  readonly onPath: boolean;
  readonly current: boolean;
  readonly dim: boolean;
}

function CardFace({ node }: { node: DemoFlowNode }) {
  return (
    <>
      <span className={cn('flex h-[30px] items-center gap-1.5 text-[0.84rem] font-semibold', HEAD_TONE[node.type], node.type === 'message' && node.channel === 'email' && 'text-ident-a')}>
        <Icon type={node.type} channel={node.channel} />
        {node.type === 'message' ? (node.channel === 'email' ? 'Email' : 'WhatsApp') : NODE_TYPE_LABEL[node.type]}
        {node.type === 'message' && <span className="ml-auto font-mono text-[0.62rem] font-medium uppercase tracking-[0.08em] text-muted">message</span>}
      </span>
      {node.type === 'trigger' && (
        <span className="mt-2 block">
          <span className="block text-[0.88rem] font-semibold leading-snug text-text">{node.summary}</span>
          <span className="mt-2 flex flex-wrap gap-1.5">
            <span className="rounded-full border border-brand-b/45 bg-brand-a/20 px-2 py-px font-mono text-[0.64rem] text-brand-ink">rule segment</span>
            <span className="rounded-full border border-line-strong bg-white/[0.06] px-2 py-px font-mono text-[0.64rem] text-muted">once per person</span>
          </span>
          <span className="mt-2 block text-[0.72rem] leading-snug text-muted">Quiet hours 21:00–09:00 IST · 2 messages / 7 days</span>
        </span>
      )}
      {node.type === 'message' && node.channel === 'whatsapp' && (
        <>
          <span className="mb-2.5 mt-2 line-clamp-4 block h-[96px] rounded-[4px_12px_12px_12px] border border-good/32 bg-good/14 px-3 py-2 text-[0.8rem] leading-normal text-text">
            <Body text={node.body ?? ''} />
          </span>
          {(node.buttons ?? []).map((label) => (
            <span key={label} className="mt-[7px] flex h-[34px] items-center justify-center rounded-sm border border-white/16 bg-white/[0.06] text-[0.8rem] font-medium text-text">
              {label}
            </span>
          ))}
          {node.ports.some((port) => port.id === 'noreply') && (
            <span className="mt-[7px] flex h-[34px] items-center justify-center rounded-sm border border-dashed border-white/16 text-[0.78rem] text-muted">No reply in 48 hours</span>
          )}
        </>
      )}
      {node.type === 'message' && node.channel === 'email' && (
        <span className="mt-2 block h-[90px] rounded-sm border border-ident-a/30 bg-ident-a/8 px-3 py-2">
          <span className="block text-[0.8rem] font-semibold text-text">{node.subject}</span>
          <span className="mt-1 line-clamp-2 block text-[0.76rem] leading-snug text-muted">{node.body}</span>
        </span>
      )}
      {(node.type === 'update' || node.type === 'goal' || node.type === 'exit') && (
        <span className="mt-2 block text-[0.84rem] leading-snug text-text">{node.summary}</span>
      )}
    </>
  );
}

function RoundFace({ node, current }: { node: DemoFlowNode; current: boolean }) {
  return (
    <>
      <span
        className={cn(
          'mx-auto grid size-[54px] place-items-center rounded-full bg-brand-gradient text-white shadow-brand',
          current && 'ring-4 ring-brand-b/50 motion-safe:animate-pulse',
        )}
      >
        <Icon type={node.type} />
      </span>
      <span className="mt-1.5 block h-[22px] text-[0.78rem] font-medium text-text">{node.summary}</span>
      {node.type === 'wait' ? (
        <span className="mx-auto mt-[6px] flex h-[34px] w-32 items-center justify-center rounded-[10px] border border-line-strong bg-surface-2 text-[0.76rem] text-muted">
          <b className="font-semibold text-text">Next 10:00</b>&nbsp;IST
        </span>
      ) : (
        <span className="mx-auto mt-[6px] block w-32 overflow-hidden rounded-[10px] border border-line-strong bg-surface-2">
          {node.ports.map((port) => (
            <span
              key={port.id}
              className={cn(
                'flex h-9 items-center justify-center border-t border-line text-[0.78rem] first:border-t-0',
                port.holdout ? 'bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.025)_0_6px,transparent_6px_12px)] text-muted' : 'text-text',
                port.id === 'yes' && 'text-good',
                port.id === 'no' && 'text-bad',
              )}
            >
              {port.label}
            </span>
          ))}
        </span>
      )}
    </>
  );
}

function NodeButton({ node, look, onSelect }: { node: DemoFlowNode; look: NodeLook; onSelect: () => void }) {
  const round = isRound(node.type);
  return (
    <button
      type="button"
      aria-pressed={look.selected}
      aria-label={`${NODE_TYPE_LABEL[node.type]}: ${node.summary}${look.current ? ' (the selected person is here)' : ''}`}
      onClick={onSelect}
      className={cn(
        'absolute block text-left transition-[opacity,border-color,box-shadow] duration-200',
        focus,
        round
          ? 'rounded-base text-center'
          : cn(
              'rounded-[14px] border bg-surface-2 px-3.5 pb-3 pt-2 shadow-[0_16px_34px_-20px_rgba(0,0,0,0.9)] hover:border-white/28',
              node.type === 'goal' ? 'border-good/40' : node.type === 'exit' ? 'border-bad/35' : 'border-line-strong',
              look.onPath && 'border-brand-b/60',
              look.selected && 'border-brand-b shadow-selected',
              look.current && 'border-brand-b ring-4 ring-brand-b/35 motion-safe:animate-pulse',
            ),
        round && look.selected && '[&>span:first-child]:shadow-selected',
        look.dim && 'opacity-60',
      )}
      style={{ left: node.x, top: node.y, width: round ? ROUND_W : node.w, height: node.h }}
    >
      {round ? <RoundFace node={node} current={look.current} /> : <CardFace node={node} />}
    </button>
  );
}

function Drawer({ node }: { node: DemoFlowNode | undefined }) {
  if (node === undefined) {
    return <p className="text-[0.86rem] leading-relaxed text-muted">Press a step to see its settings.</p>;
  }
  return (
    <div>
      <p className="flex items-center gap-2 font-mono text-[0.64rem] uppercase tracking-[0.13em] text-brand-ink">
        <Icon type={node.type} channel={node.channel} />
        {NODE_TYPE_LABEL[node.type]}
        {node.channel !== undefined && ` · ${node.channel === 'email' ? 'email' : 'WhatsApp'}`}
      </p>
      <h4 className="mt-1.5 text-[1.05rem] font-semibold text-text">{node.summary}</h4>
      {node.body !== undefined && (
        <p className="mt-2.5 rounded-sm border border-line bg-white/[0.035] px-3 py-2 text-[0.84rem] leading-relaxed text-text">
          {node.subject !== undefined && <span className="mb-1 block font-semibold">{node.subject}</span>}
          <Body text={node.body} />
        </p>
      )}
      <dl className="mt-3 grid gap-2 text-[0.82rem]">
        {node.config.map(([key, value]) => (
          <div key={key} className="border-t border-dashed border-line pt-2">
            <dt className="text-muted">{key}</dt>
            <dd className="text-text">{value}</dd>
          </div>
        ))}
        {node.ports.length > 0 && (
          <div className="border-t border-dashed border-line pt-2">
            <dt className="text-muted">Paths out</dt>
            <dd className="text-text">
              {node.ports
                .map((port) => {
                  const to = FLOW_EDGES.find((edge) => edge.from === node.id && edge.port === port.id)?.to;
                  const target = to === undefined ? undefined : nodeById(to);
                  return `${port.label} → ${target === undefined ? 'nothing (held out)' : target.summary}`;
                })
                .join(' · ')}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}

export function FlowDemo({ active = true }: { readonly active?: boolean } = {}) {
  const [selectedId, setSelectedId] = useState<string>('reminder');
  const [personId, setPersonId] = useState<string>(SAMPLE_PEOPLE[0]?.id ?? 'priya');
  const [revealed, setRevealed] = useState(0);
  const [running, setRunning] = useState(false);
  const [view, setView] = useState({ x: 16, y: 16, scale: 0.5 });
  const viewportRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: number; x: number; y: number } | null>(null);

  const person = SAMPLE_PEOPLE.find((candidate) => candidate.id === personId) ?? SAMPLE_PEOPLE[0];
  const steps = useMemo(() => (person === undefined ? [] : walk(person)), [person]);
  const shown = steps.slice(0, revealed);
  const simulating = revealed > 0;
  const currentId = shown.at(-1)?.nodeId;
  const visited = new Set(shown.map((step) => step.nodeId));
  const hotEdges = new Set(shown.map((step) => step.edgeId).filter((id): id is string => id !== null));

  const fit = (): void => {
    const viewport = viewportRef.current;
    if (viewport === null || viewport.clientWidth === 0) return;
    const scale = clampScale(Math.min(viewport.clientWidth / CANVAS.w, viewport.clientHeight / CANVAS.h) * 0.96);
    setView({ scale, x: (viewport.clientWidth - CANVAS.w * scale) / 2, y: (viewport.clientHeight - CANVAS.h * scale) / 2 });
  };

  /* Open readable rather than whole: at "fit" the 2000px canvas is drawn at
     about 40% and its words are too small to read, so the first view is the
     start of the flow at 72%, vertically centred, and Fit shows the whole. */
  // Re-run when the tab is shown: a hidden panel measures 0 wide.
  useEffect(() => {
    if (!active) return;
    const viewport = viewportRef.current;
    if (viewport === null || viewport.clientWidth === 0) return;
    const scale = 0.72;
    setView({ scale, x: 16, y: (viewport.clientHeight - CANVAS.h * scale) / 2 });
  }, [active]);

  // Reveal the walk one step at a time; all at once when the reader asked for less motion.
  useEffect(() => {
    if (!running) return;
    if (revealed >= steps.length) {
      setRunning(false);
      return;
    }
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    const timer = window.setTimeout(() => setRevealed(reduced ? steps.length : revealed + 1), reduced ? 0 : revealed === 0 ? 50 : 900);
    return () => window.clearTimeout(timer);
  }, [running, revealed, steps.length]);

  // Keep the step being walked on screen: recentre when it falls outside the viewport.
  useEffect(() => {
    if (currentId === undefined) return;
    setSelectedId(currentId);
    const node = nodeById(currentId);
    const viewport = viewportRef.current;
    if (node === undefined || viewport === null || viewport.clientWidth === 0) return;
    setView((current) => {
      const cx = current.x + (node.x + (isRound(node.type) ? ROUND_W : node.w) / 2) * current.scale;
      const cy = current.y + (node.y + node.h / 2) * current.scale;
      const inside = cx > 40 && cx < viewport.clientWidth - 40 && cy > 40 && cy < viewport.clientHeight - 40;
      if (inside) return current;
      return { ...current, x: viewport.clientWidth / 2 - (cx - current.x), y: viewport.clientHeight / 2 - (cy - current.y) };
    });
  }, [currentId]);

  const simulate = (): void => {
    setRevealed(0);
    setRunning(true);
  };
  const reset = (): void => {
    setRunning(false);
    setRevealed(0);
  };

  const zoom = (by: number): void => {
    const viewport = viewportRef.current;
    const cx = (viewport?.clientWidth ?? 0) / 2;
    const cy = (viewport?.clientHeight ?? 0) / 2;
    setView((current) => {
      const scale = clampScale(current.scale * by);
      const ratio = scale / current.scale;
      return { scale, x: cx - (cx - current.x) * ratio, y: cy - (cy - current.y) * ratio };
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget) return;
    const action = canvasKeyAction(event.key);
    if (action === null) return;
    event.preventDefault();
    if (action.kind === 'pan') setView((current) => ({ ...current, x: current.x + action.dx, y: current.y + action.dy }));
    else if (action.kind === 'zoom') zoom(action.by);
    else fit();
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if ((event.target as Element).closest('button') !== null) return;
    drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const start = drag.current;
    if (start === null || start.id !== event.pointerId) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    drag.current = { id: start.id, x: event.clientX, y: event.clientY };
    setView((current) => ({ ...current, x: current.x + dx, y: current.y + dy }));
  };
  const endDrag = (event: PointerEvent<HTMLDivElement>): void => {
    if (drag.current?.id === event.pointerId) drag.current = null;
  };

  const lookOf = (node: DemoFlowNode): NodeLook => ({
    selected: node.id === selectedId,
    onPath: visited.has(node.id),
    current: node.id === currentId && running,
    dim: simulating && !visited.has(node.id),
  });

  const portDots = FLOW_NODES.flatMap((node) =>
    node.ports.map((port) => {
      const point = portPoint(node, port.id);
      if (point === null) return null;
      const connected = FLOW_EDGES.some((edge) => edge.from === node.id && edge.port === port.id);
      return { key: `${node.id}:${port.id}`, point, connected, holdout: port.holdout === true };
    }),
  ).filter((dot): dot is NonNullable<typeof dot> => dot !== null);

  return (
    <div id="flow-canvas" className="scroll-mt-24">
      <div className="overflow-hidden rounded-lg border border-line-strong bg-surface-1 shadow-lift">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-warn/7 px-4 py-2.5 text-[0.82rem] sm:px-5">
          <span className="text-muted">
            “{FLOW_NAME}” · made-up people · Simulate sends nothing
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3 sm:px-5">
          <span id="flow-demo-people" className="mr-1 text-[0.82rem] text-muted">
            Pick a person
          </span>
          <div role="group" aria-labelledby="flow-demo-people" className="flex flex-wrap gap-1.5">
            {SAMPLE_PEOPLE.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                aria-pressed={candidate.id === personId}
                onClick={() => {
                  setPersonId(candidate.id);
                  reset();
                }}
                className={cn(
                  'rounded-full border px-3 py-1 text-[0.8rem] transition-colors',
                  focus,
                  candidate.id === personId ? 'border-brand-b/60 bg-brand-a/28 text-white' : 'border-line-strong text-muted hover:text-text',
                )}
              >
                {candidate.name} <span className="text-[0.72rem] opacity-80">· {candidate.blurb}</span>
              </button>
            ))}
          </div>
          <span className="ml-auto flex gap-1.5">
            <button type="button" onClick={simulate} className={cn(toolButton, 'border-transparent bg-brand-gradient text-white shadow-brand hover:brightness-110')}>
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <path d="M7 5l11 7-11 7z" fill="currentColor" />
              </svg>
              {simulating ? 'Simulate again' : 'Simulate'}
            </button>
            {simulating && (
              <button type="button" onClick={reset} className={toolButton}>
                Reset
              </button>
            )}
          </span>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="relative">
            {/* The canvas, from 640px. */}
            <div
              ref={viewportRef}
              tabIndex={0}
              role="region"
              aria-label="Flow canvas. Drag, or focus it and use the arrow keys, to move around; plus and minus zoom; 0 fits it to the view."
              onKeyDown={onKeyDown}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              className={cn('relative hidden h-[460px] cursor-grab touch-none overflow-hidden bg-surface-1 active:cursor-grabbing sm:block lg:h-[540px]', 'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-b')}
            >
              <div
                className="absolute left-0 top-0 origin-top-left bg-dot-grid"
                style={{ width: CANVAS.w, height: CANVAS.h, transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
              >
                <svg width={CANVAS.w} height={CANVAS.h} aria-hidden="true" focusable="false" className="pointer-events-none absolute left-0 top-0 overflow-visible">
                  <defs>
                    <marker id="flow-demo-arrow" viewBox="0 0 10 10" refX="8.5" refY="5" markerUnits="userSpaceOnUse" markerWidth="11" markerHeight="11" orient="auto-start-reverse">
                      <path d="M1 1.5 L8.5 5 L1 8.5" fill="none" className="stroke-text" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </marker>
                    <marker id="flow-demo-arrow-hot" viewBox="0 0 10 10" refX="8.5" refY="5" markerUnits="userSpaceOnUse" markerWidth="12" markerHeight="12" orient="auto-start-reverse">
                      <path d="M1 1.5 L8.5 5 L1 8.5" fill="none" className="stroke-brand-ink" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </marker>
                  </defs>
                  {FLOW_EDGES.map((edge) => {
                    const from = nodeById(edge.from);
                    const to = nodeById(edge.to);
                    const start = from === undefined ? null : portPoint(from, edge.port);
                    if (start === null || to === undefined) return null;
                    const hot = hotEdges.has(edge.id);
                    return (
                      <path
                        key={edge.id}
                        d={curve(start, inPoint(to))}
                        fill="none"
                        strokeWidth={hot ? 2.6 : 1.8}
                        markerEnd={`url(#flow-demo-arrow${hot ? '-hot' : ''})`}
                        className={cn('transition-opacity duration-200', hot ? 'stroke-brand-ink' : 'stroke-text/55', simulating && !hot && 'opacity-25')}
                      />
                    );
                  })}
                  {portDots.map((dot) =>
                    dot.holdout ? (
                      <circle key={dot.key} cx={dot.point.x} cy={dot.point.y} r={9} className="fill-surface-1 stroke-text/40" strokeDasharray="2 2" />
                    ) : (
                      <circle key={dot.key} cx={dot.point.x} cy={dot.point.y} r={6} className={dot.connected ? 'fill-text' : 'fill-surface-1 stroke-text/40'} />
                    ),
                  )}
                </svg>
                {FLOW_NODES.map((node) => (
                  <NodeButton key={node.id} node={node} look={lookOf(node)} onSelect={() => setSelectedId(node.id)} />
                ))}
              </div>

              <div className="absolute bottom-3 left-3 flex gap-1.5">
                <button type="button" aria-label="Zoom out" onClick={() => zoom(1 / 1.15)} className={cn(toolButton, 'w-8 bg-surface-2 px-0')}>
                  −
                </button>
                <button type="button" aria-label="Zoom in" onClick={() => zoom(1.15)} className={cn(toolButton, 'w-8 bg-surface-2 px-0')}>
                  +
                </button>
                <button type="button" onClick={fit} className={cn(toolButton, 'bg-surface-2')}>
                  Fit
                </button>
              </div>
            </div>

            {/* Below 640px: the same steps as a list. */}
            <ol aria-label={`The steps of “${FLOW_NAME}”`} className="grid gap-2 p-4 sm:hidden">
              {FLOW_NODES.map((node) => {
                const look = lookOf(node);
                return (
                  <li key={node.id}>
                    <button
                      type="button"
                      aria-pressed={look.selected}
                      onClick={() => setSelectedId(node.id)}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-base border bg-surface-2 px-3 py-2.5 text-left',
                        focus,
                        look.selected ? 'border-brand-b shadow-selected' : look.onPath ? 'border-brand-b/60' : 'border-line-strong',
                        look.dim && 'opacity-60',
                      )}
                    >
                      <span className={HEAD_TONE[node.type]}>
                        <Icon type={node.type} channel={node.channel} />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-mono text-[0.62rem] uppercase tracking-[0.1em] text-muted">{NODE_TYPE_LABEL[node.type]}</span>
                        <span className="block text-[0.86rem] text-text">{node.summary}</span>
                      </span>
                      {look.current && <span className="ml-auto text-[0.72rem] text-brand-ink">here now</span>}
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="grid content-start gap-5 border-t border-line p-4 sm:p-5 lg:border-l lg:border-t-0">
            <div aria-live="polite">
              <Drawer node={nodeById(selectedId)} />
            </div>
            <div>
              <p className="font-mono text-[0.64rem] uppercase tracking-[0.13em] text-muted">
                {simulating ? `${person?.name ?? ''}’s walk` : 'Simulation'}
              </p>
              {simulating ? (
                <ol aria-live="polite" className="mt-2 grid gap-2">
                  {shown.map((step, index) => (
                    <li key={index} className="rounded-sm border border-line bg-white/[0.03] px-2.5 py-2 text-[0.8rem] leading-snug">
                      <span className="block font-mono text-[0.66rem] text-muted">
                        {step.at} · {nodeById(step.nodeId)?.summary}
                      </span>
                      <span className="text-text">{step.line}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="mt-2 text-[0.84rem] leading-relaxed text-muted">Pick a person, press Simulate.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
